import { Component } from '@theme/component'

const FAQ_TAB_SELECTOR = '.faq__tab'
const FAQ_HASH_PREFIX = 'faq-'

const FAQ_FALLBACK_MS = 2000

class FaqPanelError extends Error {
  /**
   * @param {number} index
   * @param {number} total
   * @param {unknown} received
   */
  constructor(index, total, received) {
    const tag =
      received instanceof Element ? received.tagName.toLowerCase() : 'nothing'
    super(
      `faq-tabs found ${tag} at faqPanel[${index}] of ${total}, expected an element with ref="faqPanel[]"`
    )
  }
}

class FaqTabs extends Component {
  #isSwitching = false

  /** @param {Event} event */
  #onTabClick = (event) => {
    const target = event.target
    if (!(target instanceof Element)) {
      return
    }

    const tab = target.closest(FAQ_TAB_SELECTOR)
    const index = tab === null ? -1 : this.refs.faqTab.indexOf(tab)
    const isCurrent = tab?.getAttribute('aria-current') === 'true'
    if (index === -1 || isCurrent || this.#isSwitching) {
      return
    }

    this.#switchTo(index)
    this.#writeHash(index)
  }

  connectedCallback() {
    super.connectedCallback()
    if (!(this.refs.faqPanel?.length && this.refs.faqTab?.length)) {
      return
    }

    this.addEventListener('click', this.#onTabClick)
    this.#select(this.#indexOfHandle(this.#handleFromHash()))
    this.dataset.ready = 'true'
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.#onTabClick)
  }

  /** @param {number} index */
  #switchTo(index) {
    const outgoing = this.refs.faqPanel.find((panel) =>
      panel.hasAttribute('data-active')
    )

    if (!(outgoing instanceof HTMLElement) || this.#prefersReducedMotion()) {
      this.#select(index)
      return
    }

    this.#markTabs(index)
    this.#crossfade(outgoing, this.#panelAt(index))
  }

  /**
   * @param {HTMLElement} outgoing
   * @param {HTMLElement} incoming
   */
  #crossfade(outgoing, incoming) {
    this.#isSwitching = true

    const panelTop = outgoing.offsetTop
    const fromHeight = outgoing.offsetHeight

    incoming.setAttribute('data-active', '')
    const toHeight = incoming.offsetHeight

    outgoing.style.insetBlockStart = `${panelTop}px`
    outgoing.setAttribute('data-leaving', '')
    outgoing.toggleAttribute('inert', true)
    outgoing.removeAttribute('data-active')

    incoming.style.blockSize = `${fromHeight}px`
    void incoming.offsetHeight
    incoming.setAttribute('data-animating', '')
    void incoming.offsetHeight
    incoming.style.blockSize = `${toHeight}px`

    this.#whenMotionEnds(outgoing, incoming).then(() => {
      this.#settle(outgoing, incoming)
    })
  }

  /**
   * @param {HTMLElement} outgoing
   * @param {HTMLElement} incoming
   * @returns {Promise<unknown>}
   */
  #whenMotionEnds(outgoing, incoming) {
    const running = [
      ...outgoing.getAnimations({ subtree: true }),
      ...incoming.getAnimations({ subtree: true })
    ].map((animation) => animation.finished)

    const guard = new Promise((resolve) => {
      setTimeout(resolve, FAQ_FALLBACK_MS)
    })

    return Promise.race([Promise.allSettled(running), guard])
  }

  /**
   * @param {HTMLElement} outgoing
   * @param {HTMLElement} incoming
   */
  #settle(outgoing, incoming) {
    outgoing.removeAttribute('data-leaving')
    outgoing.toggleAttribute('inert', false)
    outgoing.style.insetBlockStart = ''
    this.#collapseQuestions(outgoing)

    incoming.removeAttribute('data-animating')
    incoming.style.blockSize = ''
    this.#isSwitching = false
  }

  /** @param {number} index */
  #select(index) {
    const position = index === -1 ? 0 : index

    this.refs.faqPanel.forEach((panel, at) => {
      const isCurrent = at === position
      panel.toggleAttribute('data-active', isCurrent)
      if (!isCurrent) {
        this.#collapseQuestions(panel)
      }
    })

    this.#markTabs(position)
  }

  /** @param {number} index */
  #markTabs(index) {
    this.refs.faqTab.forEach((tab, at) => {
      const isCurrent = at === index
      tab.setAttribute('aria-expanded', isCurrent ? 'true' : 'false')
      if (isCurrent) {
        tab.setAttribute('aria-current', 'true')
        return
      }
      tab.removeAttribute('aria-current')
    })
  }

  /**
   * @param {number} index
   * @returns {HTMLElement}
   */
  #panelAt(index) {
    const panel = this.refs.faqPanel[index]
    if (!(panel instanceof HTMLElement)) {
      throw new FaqPanelError(index, this.refs.faqPanel.length, panel)
    }

    return panel
  }

  /** @param {Element} panel */
  #collapseQuestions(panel) {
    for (const details of panel.querySelectorAll('details[open]')) {
      details.removeAttribute('open')
    }
  }

  /** @returns {boolean} */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /** @param {number} index */
  #writeHash(index) {
    const handle = this.#handleAt(index)
    if (!handle) {
      return
    }

    history.replaceState(null, '', `#${FAQ_HASH_PREFIX}${handle}`)
  }

  /**
   * @param {number} index
   * @returns {string}
   */
  #handleAt(index) {
    const panel = this.refs.faqPanel[index]
    if (!(panel instanceof HTMLElement)) {
      return ''
    }

    return panel.dataset.faqGroup ?? ''
  }

  /**
   * @param {string} handle
   * @returns {number}
   */
  #indexOfHandle(handle) {
    if (!handle) {
      return -1
    }

    return this.refs.faqPanel.findIndex(
      (panel) =>
        panel instanceof HTMLElement && panel.dataset.faqGroup === handle
    )
  }

  /** @returns {string} */
  #handleFromHash() {
    const raw = window.location.hash.slice(1)
    if (!raw.startsWith(FAQ_HASH_PREFIX)) {
      return ''
    }

    try {
      return decodeURIComponent(raw.slice(FAQ_HASH_PREFIX.length))
    } catch {
      return ''
    }
  }
}

if (!customElements.get('faq-tabs')) {
  customElements.define('faq-tabs', FaqTabs)
}
